import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Download, Upload, X } from "lucide-react";
import { useState, useRef } from "react";
import { useDownloadTemplateOperations, useCreateTemplateOperations } from "@features/mx/meterReads/hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useToast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useParams } from "react-router-dom";

interface UploadMissingReadingsDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function UploadMissingReadingsDialog({
  open,
  setOpen,
}: UploadMissingReadingsDialogProps) {
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const remoteUtilityId = useRemoteUtilityId();
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const downloadTemplateOperations = useDownloadTemplateOperations();
  const createTemplateOperations = useCreateTemplateOperations();

  /** 🔽 Download template from backend */
  const handleDownloadTemplate = () => {
    if (!id) {
      toast({
        title: "Error",
        description: "Run ID is missing for template download.",
        variant: "destructive",
      });
      return;
    }

    downloadTemplateOperations.mutate(
      {
        remote_utility_id: remoteUtilityId,
        run_id: id,
      },
      {
        onSuccess: () => {
          toast({
            title: "Success",
            description: "Template downloaded successfully.",
            variant: "default",
          });
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to download template.",
            variant: "destructive",
          });
        },
      }
    );
  };

  /** ⬆️ Upload file using same API */
  const handleUploadConfirm = () => {
    if (!uploadFile) {
      toast({
        title: "Error",
        description: "Please select a file to upload.",
        variant: "destructive",
      });
      return;
    }

    const payload = {
      file: uploadFile,
      remote_utility_id: remoteUtilityId,
      ...(id && { run_id: id }),
    };

    createTemplateOperations.mutate(payload, {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "File uploaded successfully.",
          variant: "default",
        });
        setUploadFile(null);
        setOpen(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      },
      onError: () => {
        toast({
          title: "Error",
          description: "Failed to upload file.",
          variant: "destructive",
        });
      },
    });
  };

  const handleClose = () => {
    setUploadFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setOpen(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setUploadFile(file);
  };

  const handleRemoveFile = () => {
    setUploadFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Upload Missing Readings</DialogTitle>
          <DialogDescription>
            Download the CSV template, fill in missing readings, and upload it here.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Download Section */}
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              disabled={downloadTemplateOperations.isPending}
              className="w-full sm:w-fit"
            >
              <Download className="h-4 w-4 mr-2" />
              {downloadTemplateOperations.isPending ? "Downloading..." : "Download Template"}
            </Button>
            <p className="text-xs text-muted-foreground">
              Download and fill the missing readings file. Keep column headers unchanged.
            </p>
          </div>

          {/* Upload Section */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Upload filled file</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              className="block w-full rounded-md border border-input bg-background p-2 text-sm"
            />
            {uploadFile ? (
              <div className="flex justify-between items-center bg-blue-50 border border-blue-200 p-2 rounded-md">
                <p className="text-xs text-blue-800">
                  {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
                </p>
                <button
                  onClick={handleRemoveFile}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No file selected.</p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleUploadConfirm}
            disabled={!uploadFile || createTemplateOperations.isPending}
          >
            {createTemplateOperations.isPending ? (
              <>
                <Upload className="w-4 h-4 mr-2 animate-spin" /> Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" /> Upload
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
